<template>
  <ResponsiveLayout :progress="progress">
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>

    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__insurance_search__title_web') }}
        </p>
      </div>

      <div
        v-if="loadingInsurances"
        class="insurance-selection__loading"
        data-qa="insurance-selection-loading"
      >
        <KDSSpinner />
      </div>

      <div
        v-else
        class="insurance-selection__list"
      >
        <fieldset class="insurance-selection__fieldset">
          <div
            v-for="insurance in popularInsurances"
            :key="insurance.key"
            data-qa="insurance-radio-item"
            :class="[
              'quiz-radio',
              { 'quiz-radio--checked': selectedKey === insurance.key },
            ]"
            @click="selectInsurance(insurance.key)"
          >
            <label class="quiz-radio__label kds-typography-title-medium">
              {{ insurance.title }}
            </label>
            <div class="quiz-radio__layout">
              <CustomRadio :checked="selectedKey === insurance.key" />
            </div>
          </div>

          <div
            data-qa="insurance-radio-item"
            :class="[
              'quiz-radio',
              { 'quiz-radio--checked': selectedKey === 'other' },
            ]"
            @click="selectInsurance('other')"
          >
            <label class="quiz-radio__label kds-typography-title-medium">
              {{ $t('eu__insurance_search__other_insurance') }}
            </label>
            <div class="quiz-radio__layout">
              <CustomRadio :checked="selectedKey === 'other'" />
            </div>
          </div>
        </fieldset>

        <p
          v-if="fieldAttrs.insuranceSearch.errors?.length"
          data-qa="insurance-selection-error"
          class="kds-typography-paragraph-medium kds-color-text-error"
        >
          {{ $t(fieldAttrs.insuranceSearch.errors[0]) }}
        </p>

        <form
          id="insurance-selection-search-form"
          :class="{
            'insurance-selection__search--hidden': selectedKey !== 'other',
          }"
          :inert="selectedKey !== 'other'"
          @submit.prevent
        >
          <SearchSelectField
            v-bind="fieldAttrs.insuranceSearch"
            v-model:selected-item="searchedInsurance"
            :disabled="submitAttrs.loading"
            data-qa="insurance-search-field"
            :label="$t('eu__insurance_search__field_label')"
            :search-fn="searchInsuranceOptions"
            :item-key="itemKey"
            :item-title="itemTitle"
            :no-results-title="$t('eu__insurance_search__not_found_title')"
            :no-results-description="
              $t('eu__insurance_search__not_found_description')
            "
            :search-error-title="$t('generic__error_title')"
            :search-error-description="
              $t('eu__insurance_search__search_error_description', {
                email: env.VITE_SUPPORT_EMAIL,
              })
            "
          />
        </form>
      </div>

      <BaseBanner
        v-if="showDigaNotSupported"
        variant="warning"
        data-qa="insurance-selection-diga-not-supported"
      >
        <p>{{ $t('eu__corporate_search__diga_not_supported__description') }}</p>
      </BaseBanner>

      <BaseBanner
        v-else-if="showGenericError"
        variant="warning"
        :title="$t('generic__error_title')"
      >
        <p
          v-html="
            $t('generic__error_message', { email: env.VITE_SUPPORT_EMAIL })
          "
        />
      </BaseBanner>
    </div>

    <template #actions>
      <BaseButton
        v-if="showDigaNotSupported"
        data-qa="insurance-selection-diga-support"
        :text="$t('eu__corporate_search__submit_button__diga_not_supported')"
        @click="redirectToSupportPage"
      />
      <BaseButton
        v-else
        type="submit"
        :loading="submitAttrs.loading"
        data-qa="insurance-selection-submit"
        :text="$t('eu__corporate_search__primary_cta')"
        @click="handleSubmit"
      />
    </template>
  </ResponsiveLayout>
  <PrescriptionSupportModal
    ref="supportModalRef"
    show-skip
    @skip="navigateNext('supportSkipped')"
  />
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import * as apiRequest from '@shared/api-endpoints'
import { isPrivateWithContract } from '@shared/data/models/corporate.js'
import env from '@shared/env.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useUserStore from '@shared/stores/useUserStore.js'
import useNavigation from '@eu/app/composables/useNavigation'
import useInsuranceSubmission from '@eu/app/composables/useInsuranceSubmission'
import ROUTE from '@eu/app/router/appModule/names'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import { trackInsuranceListLoaded } from '@eu/app/helpers/trackInsuranceListLoaded'
import { preloadImage } from '@shared/utils/preloadImage'
import publicInsuranceImageDe from '@shared/assets/img/insurance_covered_public_de.png'
import publicInsuranceImageEn from '@shared/assets/img/insurance_covered_public_en.png'
import privateInsuranceImage from '@shared/assets/img/insurance_covered_private.svg'

import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import SearchSelectField from '@shared/components/form/SearchSelectField.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'
import CustomRadio from '@shared/components/CustomRadio.vue'
import KDSSpinner from '@shared/components/KDSSpinner.vue'
import PrescriptionSupportModal from '@eu/app/components/PrescriptionSupportModal.vue'

const props = defineProps({
  // When the route started rendering; the load-time event counts from here.
  enteredAt: {
    type: Number,
    default: () => performance.now(),
  },
})

const POPULAR_COUNT = 5
// Request extra to guarantee 5 after filtering out private-with-contract insurers.
const popularCorporatesPromise = apiRequest.getPopularCorporates({ limit: 9 })

const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.INSURANCE_SELECTION,
)
const { progress } = useOnboardingProgress()
const corporateStore = useCorporateStore()
const userStore = useUserStore()

const popularInsurances = ref([])
const selectedKey = ref(null)
const searchedInsurance = ref(null)
const loadingInsurances = ref(true)
const supportModalRef = ref(null)

const resolvedInsurance = computed(() => {
  if (selectedKey.value === 'other') return searchedInsurance.value
  return (
    popularInsurances.value.find((i) => i.key === selectedKey.value) ?? null
  )
})

const {
  form,
  fieldAttrs,
  submitAttrs,
  submit,
  showGenericError,
  showDigaNotSupported,
} = useInsuranceSubmission({
  selectedInsurance: resolvedInsurance,
  fieldName: 'insuranceSearch',
  navigateNext,
})

onMounted(async () => {
  try {
    const result = await popularCorporatesPromise
    popularInsurances.value = (result?.data?.corporates ?? [])
      .filter((i) => !isPrivateWithContract(i.key))
      .slice(0, POPULAR_COUNT)
  } catch (error) {
    console.error(error)
    showGenericError.value = true
    popularInsurances.value = []
  } finally {
    const saved = corporateStore.corporate
    if (saved) {
      const isPopular = popularInsurances.value.some((i) => i.key === saved.key)
      if (isPopular) {
        selectedKey.value = saved.key
      } else {
        selectedKey.value = 'other'
        searchedInsurance.value = saved
        form.value.insuranceSearch = saved.title
      }
    }

    loadingInsurances.value = false
  }

  if (showGenericError.value) return
  await nextTick()
  trackInsuranceListLoaded(props.enteredAt, popularInsurances.value.length)
})

watch(
  () => corporateStore.corporate,
  (corporate) => {
    if (!corporate) return
    if (corporate.insuranceType !== 'private') {
      preloadImage(
        userStore.lang.startsWith('de')
          ? publicInsuranceImageDe
          : publicInsuranceImageEn,
      )
    } else {
      preloadImage(privateInsuranceImage)
    }
  },
  { immediate: true },
)

const selectInsurance = (key) => {
  selectedKey.value = key

  // Clear any previous submit error once the user changes the selection.
  if (fieldAttrs.value.insuranceSearch.errors?.length) {
    fieldAttrs.value.insuranceSearch.onInput()
  }

  if (key !== 'other') {
    searchedInsurance.value = null
    form.value.insuranceSearch = ''
  }
}

const itemKey = (item) => item?.key
const itemTitle = (item) => item?.title

const searchInsuranceOptions = async (query, abortSignal) => {
  const result = await apiRequest.searchCorporate(query, '', abortSignal)
  return result?.data?.corporates ?? []
}

const handleSubmit = () => submit()

const redirectToSupportPage = () => {
  supportModalRef.value.open()
}
</script>

<style lang="scss">
@use '@eu/app/views/css/insuranceSelectionList.scss';

.insurance-selection__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.insurance-selection__search--hidden {
  visibility: hidden;
}
</style>
